import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { Modal } from "./Modal";
import { ActionButton, GhostButton, FormField, PillBadge, inputClass } from "./Primitives";
import { GeofenceMap } from "./GeofenceMap";
import { Plus, Pencil, Trash2, Save, X, Radio } from "lucide-react";
import { toast } from "sonner";

const emptyStop = {
  name: "",
  instructions: "",
  description: "",
  lat: "",
  lng: "",
  geofence_radius_m: 30,
  nfc_tag_id: "",
  frequency_minutes: 60,
  order_index: 0,
  active: true,
};

/**
 * Manage tour stops attached to a site.
 *
 * Props:
 *   site: { id, name, lat, lng, geofence_radius_m, ... }
 *   onClose: () => void
 */
export default function TourStopsModal({ site, onClose }) {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyStop);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sites/${site.id}/stops`);
      setStops(r.data || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load tour stops"));
    } finally { setLoading(false); }
  }, [site.id]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditingId("__new__");
    setForm({
      ...emptyStop,
      lat: site.lat ?? "",
      lng: site.lng ?? "",
      order_index: stops.length,
    });
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      instructions: s.instructions || "",
      description: s.description || "",
      lat: s.lat ?? "",
      lng: s.lng ?? "",
      geofence_radius_m: s.geofence_radius_m,
      nfc_tag_id: s.nfc_tag_id || "",
      frequency_minutes: s.frequency_minutes,
      order_index: s.order_index,
      active: s.active !== false,
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyStop); };

  const save = async () => {
    const payload = {
      ...form,
      lat: parseFloat(form.lat), lng: parseFloat(form.lng),
      geofence_radius_m: parseInt(form.geofence_radius_m, 10),
      frequency_minutes: parseInt(form.frequency_minutes, 10),
      order_index: parseInt(form.order_index || 0, 10),
      nfc_tag_id: form.nfc_tag_id?.trim() || null,
      instructions: form.instructions?.trim() || null,
      description: form.description?.trim() || null,
    };
    if (!payload.name?.trim()) { toast.error("Name is required"); return; }
    if (Number.isNaN(payload.lat) || Number.isNaN(payload.lng)) { toast.error("Lat / Lng required"); return; }
    try {
      if (editingId === "__new__") {
        await api.post(`/sites/${site.id}/stops`, payload);
        toast.success("Stop added");
      } else {
        await api.patch(`/sites/${site.id}/stops/${editingId}`, payload);
        toast.success("Stop updated");
      }
      cancelEdit();
      load();
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tour stop?")) return;
    try {
      await api.delete(`/sites/${site.id}/stops/${id}`);
      toast.success("Stop deleted");
      load();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  const isEditing = editingId !== null;

  return (
    <Modal title={`Tour Stops · ${site.name}`} onClose={onClose} testId="tour-stops-modal">
      <div className="space-y-4">
        {/* Existing stops list */}
        <div className="border border-[#27272A]">
          <div className="px-4 py-2 border-b border-[#27272A] flex items-center justify-between">
            <div>
              <div className="label-mute">Checkpoints</div>
              <div className="font-display text-sm uppercase tracking-tight">
                {loading ? "…" : `${stops.length} stop${stops.length === 1 ? "" : "s"}`}
              </div>
            </div>
            {!isEditing && (
              <ActionButton data-testid="add-tour-stop" onClick={startNew}>
                <Plus className="size-3.5" /> Add Stop
              </ActionButton>
            )}
          </div>
          <div className="divide-y divide-[#27272A] max-h-72 overflow-y-auto" data-testid="tour-stops-list">
            {stops.map((s, i) => (
              <div key={s.id} data-testid={`tour-stop-${s.id}`} className="px-4 py-3 grid grid-cols-12 gap-3 items-center">
                <div className="col-span-1 font-mono text-xs text-zinc-500">#{i + 1}</div>
                <div className="col-span-4">
                  <div className="font-medium text-sm">{s.name}</div>
                  {s.instructions && <div className="text-xs text-zinc-500 line-clamp-2">{s.instructions}</div>}
                </div>
                <div className="col-span-2 font-mono text-xs text-zinc-400">
                  every {s.frequency_minutes}m
                </div>
                <div className="col-span-2 font-mono text-xs text-zinc-400">
                  {s.geofence_radius_m}m radius
                </div>
                <div className="col-span-1">
                  {s.nfc_tag_id && <PillBadge tone="blue"><Radio className="size-3 inline-block" /></PillBadge>}
                  {!s.active && <PillBadge tone="red">off</PillBadge>}
                </div>
                <div className="col-span-2 flex gap-1 justify-end">
                  <GhostButton data-testid={`edit-tour-stop-${s.id}`} onClick={() => startEdit(s)}><Pencil className="size-3" /></GhostButton>
                  <GhostButton data-testid={`delete-tour-stop-${s.id}`} onClick={() => remove(s.id)}><Trash2 className="size-3 text-[#FF3B30]" /></GhostButton>
                </div>
              </div>
            ))}
            {stops.length === 0 && !loading && (
              <div className="px-4 py-8 text-center text-zinc-500 text-xs scanlines">No tour stops yet. Add one to start patrol tracking.</div>
            )}
          </div>
        </div>

        {/* Editor */}
        {isEditing && (
          <div className="border border-[#27272A] bg-[#0E0E0E]">
            <div className="px-4 py-2 border-b border-[#27272A] flex items-center justify-between">
              <div className="font-display text-sm uppercase tracking-tight">
                {editingId === "__new__" ? "New Stop" : "Edit Stop"}
              </div>
              <div className="flex gap-1">
                <ActionButton data-testid="save-tour-stop" onClick={save}><Save className="size-3.5" />Save</ActionButton>
                <GhostButton data-testid="cancel-tour-stop" onClick={cancelEdit}><X className="size-3.5" />Cancel</GhostButton>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3 p-4">
              <FormField label="Name *">
                <input data-testid="tour-stop-name" className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </FormField>
              <FormField label="Frequency (minutes) *">
                <input data-testid="tour-stop-frequency" className={inputClass} type="number" min={1} value={form.frequency_minutes} onChange={(e) => setForm({ ...form, frequency_minutes: e.target.value })} />
              </FormField>
              <FormField label="Latitude *">
                <input data-testid="tour-stop-lat" className={inputClass} type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
              </FormField>
              <FormField label="Longitude *">
                <input data-testid="tour-stop-lng" className={inputClass} type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
              </FormField>
              <FormField label="Geofence radius (m)">
                <input data-testid="tour-stop-radius" className={inputClass} type="number" min={5} max={500} value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} />
              </FormField>
              <FormField label="NFC Tag ID (optional)">
                <input data-testid="tour-stop-nfc" className={inputClass} value={form.nfc_tag_id} onChange={(e) => setForm({ ...form, nfc_tag_id: e.target.value })} placeholder="e.g. NTAG-04:A2:B1:..." />
              </FormField>
              <FormField label="Order" >
                <input data-testid="tour-stop-order" className={inputClass} type="number" min={0} value={form.order_index} onChange={(e) => setForm({ ...form, order_index: e.target.value })} />
              </FormField>
              <FormField label="Active">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input data-testid="tour-stop-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <span className="text-zinc-400">Stop is included in the patrol</span>
                </label>
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Description (shown in Detailed DAR scan table)">
                  <input
                    data-testid="tour-stop-description"
                    className={inputClass}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="e.g. Rear gate camera check — falls back to name if empty"
                  />
                </FormField>
              </div>
              <div className="md:col-span-2">
                <FormField label="Instructions">
                  <textarea data-testid="tour-stop-instructions" className={inputClass + " min-h-[80px]"} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="What should the  inspect here?" />
                </FormField>
              </div>
              {!Number.isNaN(parseFloat(form.lat)) && !Number.isNaN(parseFloat(form.lng)) && (
                <div className="md:col-span-2">
                  <GeofenceMap lat={parseFloat(form.lat)} lng={parseFloat(form.lng)} radius_m={parseInt(form.geofence_radius_m, 10) || 30} height="220px" editable onPick={(lat, lng) => setForm({ ...form, lat, lng })} />
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">click map to reposition</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
